"use client";
// components/WishlistDetailPanel.jsx

import { useEffect, useState, useRef } from "react";

const BUCKET_COLORS = [
  { bg: "#dcfce7", border: "#86efac", text: "#14532d", bar: "#22c55e" },
  { bg: "#fef9c3", border: "#fde047", text: "#854d0e", bar: "#eab308" },
  { bg: "#fff7ed", border: "#fdba74", text: "#9a3412", bar: "#f97316" },
  { bg: "#fff7ed", border: "#fdba74", text: "#9a3412", bar: "#f97316" },
  { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b", bar: "#ef4444" },
  { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b", bar: "#ef4444" },
  { bg: "#fdf4ff", border: "#d8b4fe", text: "#6b21a8", bar: "#a855f7" },
  { bg: "#eff6ff", border: "#93c5fd", text: "#1e3a8a", bar: "#3b82f6" },
];

export default function WishlistDetailPanel({ schoolID, onClose, itemMinPrice, itemMaxPrice, categories }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!schoolID) return;

    // Abort any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    // NOTE: don't clear data — keep old data visible while loading

    const params = new URLSearchParams();
    if (itemMinPrice) params.set("itemMinPrice", itemMinPrice);
    if (itemMaxPrice) params.set("itemMaxPrice", itemMaxPrice);
    if (categories?.length) params.set("categories", categories.join(","));

    fetch(`/api/wishlist/${schoolID}/items?${params.toString()}`, { signal: controller.signal })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { if (e.name !== "AbortError") setLoading(false); });

    return () => controller.abort();
  }, [schoolID, itemMinPrice, itemMaxPrice, categories]);

  const maxCount = data?.buckets ? Math.max(...data.buckets.map(b => b.count), 1) : 1;

  return (
    <div style={{
      position: "absolute", top: 0, right: 0,
      width: 380, height: "100%",
      background: "#fffbf5", borderLeft: "3px solid #fde68a",
      boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
      display: "flex", flexDirection: "column",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      zIndex: 20, animation: "slideIn .25s ease",
    }}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        background: "#fffbf5", borderBottom: "3px solid #fde68a",
        padding: "16px 16px 14px", flexShrink: 0, position: "relative",
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: 12, right: 12,
          width: 28, height: 28, borderRadius: "50%",
          background: "#fef2f2", border: "2px solid #fca5a5",
          color: "#dc2626", fontSize: 16, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
        }}>×</button>

        {!data ? (
          <div style={{ color: "#92400e", fontSize: 14, padding: "8px 0" }}>Loading...</div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, paddingLeft: 8 }}>
              <div style={{
                width: 42, height: 42, borderRadius: "50%",
                background: "linear-gradient(135deg, #f97316, #fbbf24)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 900, color: "#fff", flexShrink: 0,
                boxShadow: "0 3px 0 #ca8a04",
              }}>
                {data.teacher.titleName?.charAt(0)}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: "#dc2626", lineHeight: 1.2 }}>
                  {data.teacher.titleName} {data.teacher.lastName}'s List
                </div>
                <div style={{ fontSize: 11, color: "#92400e", marginTop: 2 }}>{data.teacher.schoolName}</div>
                <div style={{ fontSize: 10, color: "#b45309", marginTop: 1 }}>
                  {data.teacher.address?.city}, {data.teacher.address?.state} · Grade {data.teacher.grade}
                </div>
              </div>
            </div>
            {data.teacher.wishlistUrl && (
              <a href={data.teacher.wishlistUrl} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "10px 18px", background: "#FFCA28", color: "#7c2d12",
                borderRadius: 999, fontSize: 13, fontWeight: 900, textDecoration: "none",
                boxShadow: "0 4px 0 #ca8a04", width: "100%", boxSizing: "border-box",
              }}>
                🎁 Shop This List →
              </a>
            )}
          </>
        )}
      </div>

      {/* ── Bucket list ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px 24px", position: "relative" }}>

        {/* Subtle loading overlay — doesn't blank the panel */}
        {loading && data && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(255,251,245,0.6)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            paddingTop: 24, zIndex: 5, pointerEvents: "none",
          }}>
            <div style={{ fontSize: 12, color: "#f97316", fontWeight: 700, background: "#fffbf5", padding: "4px 12px", borderRadius: 999, border: "2px solid #fde68a" }}>
              Updating...
            </div>
          </div>
        )}

        {/* Initial load spinner */}
        {loading && !data && (
          <div style={{ textAlign: "center", color: "#f97316", fontSize: 14, padding: 24 }}>
            🎁 Loading wishlist...
          </div>
        )}

        {!loading && data?.buckets?.length === 0 && (
          <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: 24 }}>
            All items have been purchased! 🎉
          </div>
        )}

        {(data?.buckets || []).map((bucket, i) => {
          const c = BUCKET_COLORS[i % BUCKET_COLORS.length];
          return (
            <div key={bucket.label} style={{
              background: c.bg, border: `2px solid ${c.border}`,
              borderRadius: 14, padding: "12px 14px", marginBottom: 10,
              boxShadow: `3px 3px 0 ${c.border}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: c.text }}>{bucket.label}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#fff", background: c.bar, borderRadius: 999, padding: "2px 10px" }}>
                  {bucket.count} item{bucket.count !== 1 ? "s" : ""}
                </div>
              </div>
              {bucket.product_titles?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                  {bucket.product_titles.map((name, ni) => (
                    <span key={ni} style={{
                      fontSize: 11, fontWeight: 700,
                      background: "rgba(255,255,255,0.85)", border: `1px solid ${c.border}`,
                      borderRadius: 999, padding: "3px 10px", color: c.text,
                    }}>{name}</span>
                  ))}
                  {bucket.count > bucket.product_titles.length && (
                    <span style={{ fontSize: 11, fontWeight: 700, background: c.bar, borderRadius: 999, padding: "3px 10px", color: "#fff" }}>
                      +{bucket.count - bucket.product_titles.length} more
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}